import React, { createContext, useContext, ReactNode } from 'react';
import { AdDetails, GetPopularRequestsResponse } from '@/api/types/ads.types';
import {
  CategoryBaseInfo,
  GetRootCategoriesResponse,
} from '@/api/types/categories.types';
import { GetRegionsResponse } from '@/api/types/regions.types';

interface ServerDataContextProps {
  initialAdDetails: AdDetails | null;
  hasInitialAdNotFoundError: boolean;
  hasInitialMissingAd: boolean;
  popularRequests: GetPopularRequestsResponse | null;
  rootCategories: GetRootCategoriesResponse | null;
  currentCategory: CategoryBaseInfo | null;
  regions: GetRegionsResponse | null;
  h1: string | null;
}

const ServerDataContext = createContext<ServerDataContextProps | undefined>(
  undefined
);

export const ServerDataProvider = ({
  children,
  adDetails,
  hasAdNotFoundError,
  hasInitialMissingAd,
  popularRequests,
  rootCategories,
  currentCategory,
  regions,
  h1,
}: {
  children: ReactNode;
  adDetails?: AdDetails | null;
  hasAdNotFoundError?: boolean;
  hasInitialMissingAd?: boolean;
  popularRequests?: GetPopularRequestsResponse | null;
  rootCategories?: GetRootCategoriesResponse | null;
  currentCategory?: CategoryBaseInfo | null;
  regions?: GetRegionsResponse | null;
  h1?: string | null;
}) => {
  return (
    <ServerDataContext.Provider
      value={{
        initialAdDetails: adDetails ?? null,
        hasInitialAdNotFoundError: !!hasAdNotFoundError,
        hasInitialMissingAd: !!hasInitialMissingAd,
        h1: h1 ?? null,
        regions: regions ?? null,
        popularRequests: popularRequests ?? null,
        rootCategories: rootCategories ?? null,
        currentCategory: currentCategory ?? null,
      }}
    >
      {children}
    </ServerDataContext.Provider>
  );
};

export const useServerDataContext = () => {
  const context = useContext(ServerDataContext);
  if (!context) {
    throw new Error(
      'useServerDataContext must be used within ServerDataProvider'
    );
  }
  return context;
};

// Header can mount on pages without ServerDataProvider, we don't want to throw an error in that case
export const useOptionalServerDataContext = () => useContext(ServerDataContext);
